import React, { useState } from 'react'
import { Table, TableCell, TableHead, TableRow, TableBody, Grid2, Box, Typography, IconButton, Dialog, DialogContent, DialogContentText, DialogActions, Button, Card, CardContent } from '@mui/material'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import SubscriberDialog from './indicatorDialogs/subscriber/SubscriberDialog'
import { getFinancialIndicators, getNetworkIndicators, getQualityIndicators, getSubscriberIndicators } from 'rpqs/utils/RPQSUtils'
import NetworkDialog from './indicatorDialogs/network/NetworkDialog'
import QualityDialog from './indicatorDialogs/quality/QualityDialog'
import FinancialDialog from './indicatorDialogs/financial/FinancialDialog'
import { getYear } from 'utils/DateUtil'
import { FINANCIAL, FINANCIAL_COLOR, GREY_BG, HOVER_COLOR_ROW, NETWORK, NETWORK_COLOR, QUALITY, QUALITY_COLOR, SUBSCRIBER, SUBSCRIBER_COLOR } from 'rpqs/constants/RpqsConstant'
import { useDispatch } from 'react-redux'
import CollectivityAction from 'rpqs/actions/CollectivityAction'
import { CardTitle } from 'components/card/NewCard'

import WarningIcon from '@mui/icons-material/Warning'
import Search from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import SelectChipIndicator from './SelectChipIndicator'
import { normalizeString } from 'utils/StringUtil'
import { MAIN_RADIUS } from 'utils/constants/Theme'

const WarningBox = ({
    indicatorRow,
    year = '',
    managementEntity = {},
}) => {
    const [warningOpen, setWarningOpen] = useState(false)
    const dispatch = useDispatch()

    const currentIndicator = managementEntity?.[year]?.[indicatorRow?.indicator]

    const handleClickWarning = (e) => {
        e.stopPropagation()
        setWarningOpen(true)
    }

    const handleAcquitBtn = () => {
        const newEntity = {
            ...managementEntity,
            [year]: {
                ...managementEntity?.[year],
                [indicatorRow?.indicator]: {
                    ...currentIndicator,
                    warningAcquitted: true,
                },
            },
        }

        dispatch(CollectivityAction.updateEntity(newEntity))
        setWarningOpen(false)
    }

    // TODO: update this rule when settings page done
    const hasAlert = currentIndicator?.warningAcquitted ?
        false :
        Math.abs(indicatorRow?.[year] - indicatorRow?.[year-1]) > 1/4*indicatorRow?.[year]
    return (
        <>
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                }}
            >
                {indicatorRow?.[(year)?.toString()] || 0}
                {hasAlert ?
                    <IconButton
                        size='small'
                        sx={{ p: 1 }}
                        onClick={handleClickWarning}
                    >
                        <WarningIcon color='error' fontSize='small'/>
                    </IconButton>
                    :
                    <div/>
                }
            </Box>
            <Dialog
                onClick={(e) => e.stopPropagation()}
                open={warningOpen}
                onClose={(e) => {
                    e.stopPropagation()
                    setWarningOpen(false)
                }}
            >
                <DialogContent>
                    <Grid2 container justifyContent={'space-between'}>
                        <Typography
                            sx={{ fontWeight: 'bold' }}
                        >
                            {i18n.alert}
                        </Typography>
                        <IconButton
                            sx={{ color: 'black' }}
                            onClick={() => setWarningOpen(false)}
                        >
                            <CloseIcon/>
                        </IconButton>
                    </Grid2>
                    <DialogContentText>
                        {i18n.alarmingValue}
                    </DialogContentText>
                    <DialogContentText>
                        {i18n.wouldAcquitAlert}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        variant='contained'
                        color='error'
                        onClick={handleAcquitBtn}
                    >
                        {i18n.acquitAlert}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

WarningBox.propTypes = {
    indicatorRow: PropTypes.shape({
        indicator: PropTypes.string,
    }),
    year: PropTypes.string,
    managementEntity: PropTypes.shape({}),
}

export const CategoryPanel = ({
    managementEntity = {},
    category = '',
    year = '',
    searchValue = '',
}) => {
    const dispatch = useDispatch()
    const [dialogOpen, setDialogOpen] = useState(false)
    const [codeIndicatorPanel, setCodeIndicatorPanel] = useState('')
    const [dialogTitle, setDialogTitle] = useState('')
    const [isCategoryHover, setIsCategoryHover] = useState(false)
    const [rowIndexHover, setRowIndexHover] = useState(-1)

    const handleClickRow = (row) => {
        setCodeIndicatorPanel(row.code)
        setDialogTitle(`${(row.indicator)} - ${row.indicatorDenomination}`)
        setDialogOpen(true)
    }

    // Return indicator list, card color and dialog
    const getCategoryElements = (categ) => {
        switch (categ) {
            case SUBSCRIBER:
                return {
                    indicators: getSubscriberIndicators(),
                    color: SUBSCRIBER_COLOR,
                    dialog:
                        <SubscriberDialog
                            open={dialogOpen}
                            setOpen={setDialogOpen}
                            codeIndicator={codeIndicatorPanel}
                            dialogTitle={dialogTitle}
                        />,
                }
            case NETWORK:
                return {
                    indicators: getNetworkIndicators(),
                    color: NETWORK_COLOR,
                    dialog:
                        <NetworkDialog
                            open={dialogOpen}
                            setOpen={setDialogOpen}
                            codeIndicator={codeIndicatorPanel}
                            dialogTitle={dialogTitle}
                        />,
                }
            case QUALITY:
                return {
                    indicators: getQualityIndicators(),
                    color: QUALITY_COLOR,
                    dialog:
                        <QualityDialog
                            open={dialogOpen}
                            setOpen={setDialogOpen}
                            codeIndicator={codeIndicatorPanel}
                            dialogTitle={dialogTitle}
                        />,
                }
            case FINANCIAL:
                return {
                    indicators: getFinancialIndicators(),
                    color: FINANCIAL_COLOR,
                    dialog:
                        <FinancialDialog
                            open={dialogOpen}
                            setOpen={setDialogOpen}
                            codeIndicator={codeIndicatorPanel}
                            dialogTitle={dialogTitle}
                        />,
                }
            default:
                return {}
        }
    }

    const { color, indicators, dialog } = getCategoryElements(category)

    // Get rows with corresponding name or number indicator
    const filteredIndicators = searchValue ?
        indicators.filter((indicatorObj) => (
            normalizeString(indicatorObj.indicator).startsWith(searchValue) || normalizeString(i18n[indicatorObj.indicatorDenomination]).startsWith(searchValue)
        )) :
        indicators

    // Format rows with status of the current year
    // and indicator value of the current and previous year
    const indicatorRows = filteredIndicators.map((indicator, i) => {
        const indicatorCode = indicators[i].code
        return {
            ...indicator,
            indicatorDenomination: i18n[indicator.indicatorDenomination] || i18n.unknown,
            status: managementEntity[year]?.[indicatorCode]?.status,
            [year]: managementEntity[year]?.[indicatorCode]?.value,
            [year-1]: managementEntity[year-1]?.[indicatorCode]?.value,
        }
    })

    return (
        <>
            {indicatorRows.map((row, i) => {
                const onStatusChange = (status) => {
                    const newEntity = { ...managementEntity,
                        [year]: { ...managementEntity?.[year],
                            [row.code]: { ...managementEntity?.[year]?.[row.code],
                                status,
                            },
                        },
                    }
                    dispatch(CollectivityAction.updateEntity(newEntity))
                }

                return (
                    <TableRow
                        key={row.indicatorDenomination}
                        sx={{
                            '&:hover td': {
                                backgroundColor: i === rowIndexHover && !isCategoryHover ? HOVER_COLOR_ROW : 'transparent',
                            },
                            '& td:first-of-type': i === 0 && {
                                backgroundColor: 'transparent',
                                cursor: 'auto',
                            },
                            '& td': {
                                borderBottom: 'none',
                            },
                            borderBottom: i === indicatorRows.length-1 ? '1px grey solid' : 'none',
                            cursor: 'pointer',
                        }}
                        onMouseEnter={() => setRowIndexHover(i)}
                        onMouseLeave={() => setRowIndexHover(-1)}
                    >

                        {/* Add category panel with first row */}
                        {i === 0 &&
                            <TableCell
                                rowSpan={indicatorRows?.length}
                                width={200}
                                sx={{
                                    backgroundColor: 'white',
                                    color: 'white',
                                    cursor: 'auto',
                                    p: '8px',
                                }}
                                onMouseEnter={() => setIsCategoryHover(true)}
                                onMouseLeave={() => setIsCategoryHover(false)}
                            >
                                <Box
                                    sx={{
                                        width: '100%',
                                        height: `calc(${indicatorRows.length}*40px)`,
                                        backgroundColor: color,
                                        borderRadius: MAIN_RADIUS,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                    }}
                                >
                                    {i18n[category]}
                                </Box>
                            </TableCell>
                        }

                        <TableCell
                            onClick={() => handleClickRow(row)}
                            sx={{
                                whiteSpace: 'normal',
                                wordWrap: 'break-word',
                            }}
                        >
                            {row?.indicatorDenomination}
                        </TableCell>
                        <TableCell
                            onClick={() => handleClickRow(row)}
                        >
                            {row?.indicator}
                        </TableCell>
                        <TableCell
                            onClick={() => handleClickRow(row)}
                            sx={{
                                py: 1,
                                pr: 1,
                            }}
                        >
                            <Box
                                sx={{
                                    backgroundColor: GREY_BG,
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    px: 1,
                                    borderRadius: 1,
                                }}
                            >
                                {row?.[(year-1)?.toString()]}
                            </Box>
                        </TableCell>
                        <TableCell
                            onClick={() => handleClickRow(row)}
                        >
                            <WarningBox
                                indicatorRow={row}
                                year={year}
                                managementEntity={managementEntity}
                            />
                        </TableCell>
                        <TableCell
                            onClick={() => handleClickRow(row)}
                        >
                            {row?.unit}
                        </TableCell>
                        <TableCell
                            width={200}
                            sx={{
                                overflow: 'visible',
                                cursor: 'auto',
                            }}
                        >
                            <SelectChipIndicator
                                indicatorStatus={row?.status}
                                onChange={onStatusChange}
                            />
                        </TableCell>
                    </TableRow>)
            })}

            {dialog}
        </>
    )
}

CategoryPanel.propTypes = {
    managementEntity: PropTypes.shape({}),
    category: PropTypes.string,
    year: PropTypes.string,
    searchValue: PropTypes.string,
}

export const IndicatorPanel = ({
    managementEntity = {},
}) => {
    const [searchValue, setSearchValue] = useState('')

    const handleSearch = (e) => {
        const value = e.target.value
        setSearchValue(value)
    }

    const year = getYear(managementEntity.dateEnd)
    const categories = [SUBSCRIBER, NETWORK, QUALITY, FINANCIAL]

    return (
        <Card>
            <CardTitle
                title={
                    i18n.rpqsEdition
                }
                actions={[
                    // Search input
                    <Grid2
                        container
                        key={'search'}
                        sx={{ position: 'relative', height: '36px' }}
                        py={'4px'}
                        direction={'column'}
                        justifyContent={'center'}
                    >
                        <input
                            placeholder={i18n.search}
                            className='no-margin'
                            onChange={handleSearch}
                        />
                        <Search
                            sx={{ position: 'absolute', right: 4 }}
                        />
                    </Grid2>,
                ]}
            />
            <CardContent>
                {year &&
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    {i18n.category}
                                </TableCell>
                                <TableCell>
                                    {i18n.indicatorDenomination}
                                </TableCell>
                                <TableCell>
                                    {i18n.indicator}
                                </TableCell>
                                <TableCell>
                                    {`${i18n.value} ${(year-1).toString()}`}
                                </TableCell>
                                <TableCell>
                                    {`${i18n.value} ${(year).toString()}`}
                                </TableCell>
                                <TableCell>
                                    {i18n.unit}
                                </TableCell>
                                <TableCell>
                                    {i18n.status}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody
                            sx={{
                                '& tr:last-of-type': {
                                    borderBottom: 'none',
                                },
                            }}
                        >
                            {categories.map((category) => (
                                <CategoryPanel
                                    key={category}
                                    managementEntity={managementEntity}
                                    category={category}
                                    year={year}
                                    searchValue={searchValue}
                                />

                            ))}
                        </TableBody>
                    </Table>
                }
            </CardContent>
        </Card>
    )
}

IndicatorPanel.propTypes = {
    managementEntity: PropTypes.shape({}),
}